import { useState, useRef } from "react";
import { toast } from "sonner";
import * as api from "@/lib/api";
import { ImagePlus, Loader2, ArrowLeft, Send, Save } from "lucide-react";

export const OFFER_TYPES = [
  ["sconto_euro", "Sconto in €", "Importo (es. 50)"],
  ["sconto_perc", "Sconto %", "Percentuale (es. 30)"],
  ["mesi_gratuiti", "Mesi gratuiti", "Numero mesi (es. 2)"],
  ["porta_amico", "Porta un amico", ""],
  ["personalizzata", "Offerta personalizzata", "Etichetta (es. Pack estate)"],
];
const DURATIONS = [["24h", "24 ore", 24], ["48h", "48 ore", 48], ["72h", "72 ore", 72], ["7d", "7 giorni", 168], ["custom", "Personalizzata", 0]];
const DEFAULT_TG = "quellodellaghisa";

const toLocalInput = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const compressImage = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale);
    c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => (b ? resolve(b) : reject(new Error("compress"))), "image/jpeg", 0.86);
    URL.revokeObjectURL(img.src);
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

const emptyForm = () => ({
  title: "", subtitle: "", type: "sconto_perc", type_value: "", price_original: "", price_promo: "",
  description: "", telegram_user: DEFAULT_TG, telegram_message: "Ciao Alex, ho visto la promozione AGFit e vorrei avere maggiori informazioni.",
  duration: "48h", expires_at: "", image_path: "",
});

const Field = ({ label, children }) => (
  <label className="block space-y-1.5">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
    {children}
  </label>
);

export default function OfferForm({ offer, onDone, onCancel }) {
  const [f, setF] = useState(() => (offer ? {
    ...emptyForm(), ...offer,
    price_original: offer.price_original ?? "", price_promo: offer.price_promo ?? "",
    expires_at: offer.expires_at ? toLocalInput(new Date(offer.expires_at)) : "",
  } : emptyForm()));
  const [recompute, setRecompute] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const typeHint = OFFER_TYPES.find((t) => t[0] === f.type)?.[2];
  const durH = DURATIONS.find((d) => d[0] === f.duration)?.[2] || 0;
  const previewExp = f.duration === "custom" || (offer && !recompute)
    ? (f.expires_at ? new Date(f.expires_at) : null)
    : new Date(Date.now() + durH * 3600000);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un'immagine"); return; }
    setUploading(1);
    try {
      const blob = await compressImage(file);
      const { image_path } = await api.uploadOfferImage(blob, (p) => setUploading(Math.max(1, p)));
      set("image_path", image_path);
      toast.success("Immagine caricata");
    } catch (err) { console.error("Upload offerta:", err); toast.error("Caricamento immagine non riuscito"); }
    finally { setUploading(0); }
  };

  const pickDuration = (d) => {
    setRecompute(true);
    setF((s) => ({ ...s, duration: d, expires_at: d === "custom" ? (s.expires_at || toLocalInput(new Date(Date.now() + 48 * 3600000))) : s.expires_at }));
  };

  const submit = async () => {
    if (!f.title.trim()) { toast.error("Inserisci il titolo"); return; }
    if (f.duration === "custom" && !f.expires_at) { toast.error("Imposta data e ora di scadenza"); return; }
    if (f.duration === "custom" && new Date(f.expires_at) <= new Date()) { toast.error("La scadenza deve essere futura"); return; }
    const payload = {
      ...f,
      expires_at: f.duration === "custom" || (offer && !recompute) ? new Date(f.expires_at).toISOString() : new Date(Date.now() + durH * 3600000).toISOString(),
      recompute,
    };
    setSaving(true);
    try {
      const saved = offer ? await api.updateOffer(offer.id, payload) : await api.createOffer(payload);
      toast.success(offer ? "Offerta aggiornata" : "Offerta pubblicata");
      onDone(saved, !offer);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="offer-form">
      <button onClick={onCancel} className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-primary" data-testid="offer-form-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Torna alle offerte
      </button>

      <Field label="Immagine">
        <button type="button" onClick={() => fileRef.current?.click()} disabled={!!uploading}
          className="relative flex min-h-[160px] w-full items-center justify-center overflow-hidden rounded-xl border border-dashed border-white/15 bg-black/30 transition-colors hover:border-primary/50" data-testid="offer-image-btn">
          {f.image_path ? <img src={api.offerImageUrl(f.image_path)} alt="anteprima" className="max-h-64 w-full object-contain" data-testid="offer-image-preview" />
            : <span className="flex flex-col items-center gap-1.5 text-xs text-muted-foreground"><ImagePlus className="h-6 w-6" />Carica immagine principale</span>}
          {uploading > 0 && <span className="absolute inset-0 grid place-items-center bg-black/70 text-xs font-semibold" data-testid="offer-image-progress"><span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {uploading}%</span></span>}
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="offer-image-input" />
      </Field>

      <Field label="Titolo"><input className="lab-input" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="RIPARTI CON AGFIT" data-testid="offer-title-input" /></Field>
      <Field label="Sottotitolo"><input className="lab-input" value={f.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Il momento giusto per iniziare è adesso" data-testid="offer-subtitle-input" /></Field>

      <Field label="Tipo di offerta">
        <div className="grid grid-cols-2 gap-1.5">
          {OFFER_TYPES.map(([v, l]) => (
            <button key={v} type="button" onClick={() => set("type", v)} data-testid={`offer-type-${v}`}
              className={`rounded-lg border px-2.5 py-2 text-xs font-medium transition-colors ${f.type === v ? "border-primary/60 bg-primary/15 text-primary" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
        {typeHint && <input className="lab-input mt-2" value={f.type_value} onChange={(e) => set("type_value", e.target.value)} placeholder={typeHint} data-testid="offer-type-value-input" />}
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Prezzo originale €"><input type="number" min="0" step="0.01" className="lab-input" value={f.price_original} onChange={(e) => set("price_original", e.target.value)} placeholder="opzionale" data-testid="offer-price-original-input" /></Field>
        <Field label="Prezzo promo €"><input type="number" min="0" step="0.01" className="lab-input" value={f.price_promo} onChange={(e) => set("price_promo", e.target.value)} placeholder="opzionale" data-testid="offer-price-promo-input" /></Field>
      </div>

      <Field label="Testo dell'offerta"><textarea rows={4} className="lab-input resize-y" value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Descrivi cosa include la promozione…" data-testid="offer-description-input" /></Field>

      <div className="space-y-3 rounded-xl border border-[hsl(var(--c-teal))]/25 bg-[hsl(var(--c-teal))]/5 p-3">
        <Field label="Username Telegram"><input className="lab-input" value={f.telegram_user} onChange={(e) => set("telegram_user", e.target.value)} placeholder="quellodellaghisa" data-testid="offer-telegram-user-input" /></Field>
        <Field label="Messaggio Telegram precompilato"><textarea rows={3} className="lab-input resize-y" value={f.telegram_message} onChange={(e) => set("telegram_message", e.target.value)} data-testid="offer-telegram-message-input" /></Field>
      </div>

      <Field label="Durata promozione">
        <div className="flex flex-wrap gap-1.5">
          {DURATIONS.map(([v, l]) => (
            <button key={v} type="button" onClick={() => pickDuration(v)} data-testid={`offer-duration-${v}`}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${f.duration === v ? "border-primary/60 bg-primary/15 text-primary" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
        {f.duration === "custom" && (
          <input type="datetime-local" className="lab-input mt-2" value={f.expires_at} onChange={(e) => set("expires_at", e.target.value)} data-testid="offer-expires-input" />
        )}
        <p className="mt-2 text-[11px] text-muted-foreground" data-testid="offer-expiry-preview">
          Scadenza: <span className="font-semibold text-foreground">{previewExp ? previewExp.toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—"}</span>
          {offer && !recompute && f.duration !== "custom" && " (invariata)"}
        </p>
      </Field>

      <button onClick={submit} disabled={saving || !!uploading} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:opacity-60" data-testid="offer-publish-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : offer ? <Save className="h-4 w-4" /> : <Send className="h-4 w-4" />}
        {offer ? "Salva modifiche" : "PUBBLICA OFFERTA"}
      </button>
    </div>
  );
}
